/**
 * Pure, client-safe helpers for the G3 area page UI (lane A): hour and window wording, heatmap
 * cell colours, the sport scope's style rules, the ranked board's filter and "show all" rules,
 * the countdown timeline and the snow chart geometry. No React and no server imports, so the
 * server components and the client islands share one rule each, and every rule is unit tested.
 */
import type { AreaRow, AreaRowStats } from "@/lib/areas/row-model";
import { formatNumber } from "../area-format";
import type {
  AreaViewCountdown,
  AreaViewDay,
  AreaViewResort,
  AreaViewSnowWatch,
  AreaViewUnit,
  AreaViewWeatherDay,
} from "../area-view-types";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

// ── Words ────────────────────────────────────────────────────────────────

const UNIT_SINGULAR: Readonly<Record<AreaViewUnit, string>> = {
  beaches: "beach",
  spots: "spot",
  resorts: "resort",
  venues: "venue",
};

/** "17 beaches", "1 resort". */
export function unitCount(count: number, unit: AreaViewUnit): string {
  return `${count} ${count === 1 ? UNIT_SINGULAR[unit] : unit}`;
}

export function unitSingular(unit: AreaViewUnit): string {
  return UNIT_SINGULAR[unit];
}

export function capitalise(text: string): string {
  return text.length > 0 ? `${text[0].toUpperCase()}${text.slice(1)}` : text;
}

/** "3,230". */
export function thousands(value: number): string {
  return Math.round(value).toLocaleString("en-GB");
}

// ── Hours and windows ────────────────────────────────────────────────────

/** "8am", "12pm", "3pm" (24 reads as midnight, "12am"). */
export function hr12(hour: number): string {
  const h = ((Math.round(hour) % 24) + 24) % 24;
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
}

/** "8am to 11am". */
export function hourRange(startHour: number, endHour: number): string {
  return `${hr12(startHour)} to ${hr12(endHour)}`;
}

/** "07:26" in the timezone, or null for a missing or bad stamp. */
export function clockIn(iso: string | null, timezone: string): string | null {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: intlTimeZoneAt(timezone || "UTC", date.getTime()) }).format(date);
}

/** "UK time" for London, "local time" elsewhere (the verdict's wording). */
export function zoneWord(timezone: string): string {
  return timezone === "Europe/London" ? "UK time" : "local time";
}

/** Scores refresh every three hours: "10:26" in the timezone, from the last update, or null. */
export function nextRefreshClock(updatedISO: string | null, timezone: string): string | null {
  const ms = updatedISO ? Date.parse(updatedISO) : Number.NaN;
  if (!Number.isFinite(ms)) return null;
  return clockIn(new Date(ms + 3 * 3_600_000).toISOString(), timezone);
}

/** "Updated 07:26 UK time", or null without a stamp. */
export function updatedAt(iso: string | null, timezone: string): string | null {
  const clock = clockIn(iso, timezone);
  return clock ? `Updated ${clock} ${zoneWord(timezone)}` : null;
}

const UTC_WEEKDAY_LONG = new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "UTC" });
const UTC_WEEKDAY_SHORT = new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" });

function utcNoon(dateISO: string): Date {
  return new Date(`${dateISO}T12:00:00Z`);
}

/** "Thursday" for a calendar date. */
export function weekdayLong(dateISO: string): string {
  return UTC_WEEKDAY_LONG.format(utcNoon(dateISO));
}

/** "Thu" for a calendar date. */
export function weekdayShortOf(dateISO: string): string {
  return UTC_WEEKDAY_SHORT.format(utcNoon(dateISO));
}

/** Whole days from one calendar date to another. */
export function daysBetween(fromISO: string, toISO: string): number {
  return Math.round((utcNoon(toISO).getTime() - utcNoon(fromISO).getTime()) / 86_400_000);
}

// ── Heatmap colours (the G3 slate scale under 55, ring bands above) ─────

function hexRgb(hex: string): readonly [number, number, number] {
  return [Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16)];
}

function mix(from: string, to: string, t: number): string {
  const a = hexRgb(from);
  const b = hexRgb(to);
  const clamped = Math.max(0, Math.min(1, t));
  return `#${a.map((value, index) => Math.round(value + (b[index] - value) * clamped).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

/** Cell fill: band colours at 55+, a light slate under 25, a darker slate from 25 to 54. */
export function heatBg(score100: number): string {
  if (score100 >= 85) return "#0E9E7E";
  if (score100 >= 70) return "#C79A3A";
  if (score100 >= 55) return "#C17B5D";
  if (score100 < 25) return mix("#F4F6F2", "#C9CED4", score100 / 24);
  // Starts dark enough for white 10.5px text (the "from 8am" line) at AA: 5.3:1 at 25.
  return mix("#636C79", "#2F3746", (score100 - 25) / 29);
}

/** Cell text: navy on the light slate and the band fills, white on the mid and dark slate. */
export function heatFg(score100: number): string {
  return score100 >= 25 && score100 < 55 ? "#FFFFFF" : "#16233B";
}

/** The legend's band swatches for the highest score shown (none under 55). */
export function heatBandsShown(maxScore100: number): readonly Readonly<{ label: string; colour: string }>[] {
  const out: { label: string; colour: string }[] = [];
  if (maxScore100 >= 55) out.push({ label: "55+", colour: "#C17B5D" });
  if (maxScore100 >= 70) out.push({ label: "70+", colour: "#C79A3A" });
  if (maxScore100 >= 85) out.push({ label: "85+", colour: "#0E9E7E" });
  return out;
}

export const HEAT_SLATE_SWATCHES = ["#EBEEEC", "#D4D8DC", "#636C79", "#4D5664", "#38404F"] as const;

// ── Sport scope (one server-rendered panel per toggle sport) ────────────

/** Pure: the sport a URL selects (`?sport=` first, then a `#sport=` fragment), else the primary. */
export function resolveAreaSport(
  input: Readonly<{ param: string | null; hash: string }>,
  keys: readonly string[],
  primary: string,
): string {
  if (input.param && keys.includes(input.param)) return input.param;
  const match = /(?:^#?|&)sport=([a-z0-9-]{1,40})(?:&|$)/u.exec(input.hash);
  if (match && keys.includes(match[1])) return match[1];
  return primary;
}

/** A sport key as a CSS attribute value: lower-case letters, digits and hyphens only. */
export function cssToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/gu, "");
}

/**
 * Pure: the scope's style rules, the page's one sport visibility system. Every panel element
 * carries `data-area-panel="<key>"` (space separated keys allowed); only the scope's current sport
 * shows. `data-area-except="<key>"` hides an element for that sport (an "other sports" card hides
 * while its own sport is shown). `data-area-only="primary"` / `"other"` follow whether the current
 * sport is the page's own. Toggle chips (`data-area-chip`) take the pressed look from the same
 * attribute, so the right chip and panel show before hydration (no layout shift).
 */
export function areaSportCss(keys: readonly string[], primary: string): string {
  const scope = "[data-area-sport-root]";
  const rules: string[] = [];
  for (const raw of keys) {
    const key = cssToken(raw);
    const at = `${scope}[data-area-sport="${key}"]`;
    rules.push(`${at} [data-area-panel]:not([data-area-panel~="${key}"]){display:none!important}`);
    rules.push(`${at} [data-area-except~="${key}"]{display:none!important}`);
    // An on-demand sport whose picks loaded empty (AreaSportOnDemand marks the root): hide the
    // picks section and its jump chip rather than leave an empty, named region.
    rules.push(`${at}[data-area-empty-picks~="${key}"] [data-area-needs="picks"]{display:none!important}`);
    rules.push(`${at} [data-area-only="${key === cssToken(primary) ? "other" : "primary"}"]{display:none!important}`);
    rules.push(`${at} [data-area-chip="${key}"]{background:#FFFFFF;color:#16233B;border-color:#FFFFFF;font-weight:700;box-shadow:0 0 0 3px rgba(91,192,206,0.55)}`);
    rules.push(`${at} [data-area-chip="${key}"] [data-area-chip-icon]{color:#15727F}`);
  }
  return rules.join("");
}

/**
 * The page's focus ring (the mockups' 2px teal outline, offset 2px) for every link, button,
 * select and summary inside the area page, with the light teal variant on navy surfaces
 * (`data-area-dark`: the hero, the ranked board's header, the work band). One shared rule rather
 * than a class on each control, so nothing on the page falls back to the browser's default ring.
 */
export const AREA_FOCUS_CSS =
  "[data-area-sport-root] :is(a,button,summary,select,[tabindex]):focus-visible{outline:2px solid #15727F;outline-offset:2px}"
  + "[data-area-sport-root] [data-area-dark] :is(a,button,summary,select,[tabindex]):focus-visible{outline-color:#5BC0CE}"
  + "[data-area-sport-root] section[tabindex='-1']:focus-visible{outline:none}";

/**
 * The pre-paint script: before first paint, shows the panel a shared link selects (`?sport=` or
 * `#sport=`) by setting the scope attribute, so the page never paints the primary panel first.
 */
export function areaSportPrepaintScript(keys: readonly string[]): string {
  const allowed = JSON.stringify(keys.map(cssToken));
  return `(function(){try{var r=document.currentScript&&document.currentScript.parentNode;if(!r)return;var k=${allowed};var m=/[?&]sport=([a-z0-9-]+)/.exec(location.search)||/(?:^#|&)sport=([a-z0-9-]+)/.exec(location.hash);if(m&&k.indexOf(m[1])>=0)r.setAttribute("data-area-sport",m[1]);}catch(e){}})();`;
}

// ── Ranked board: filters and the "show all" cap ────────────────────────

export type AreaAbility = "all" | "beginner" | "experienced";

export type AreaBoardManifestRow = Readonly<{ slug: string; group: string; experiencedOnly: boolean }>;

export const AREA_BOARD_CAP_PHONE = 8;
export const AREA_BOARD_CAP_DESKTOP = 10;

export function parseAreaAbility(value: string | null): AreaAbility {
  return value === "beginner" || value === "experienced" ? value : "all";
}

function matchesAbility(row: AreaBoardManifestRow, ability: AreaAbility): boolean {
  if (ability === "beginner") return !row.experiencedOnly;
  if (ability === "experienced") return row.experiencedOnly;
  return true;
}

/** Pure: the rows a filter keeps, in rank order. An unknown group is ignored. */
export function boardVisibleRows(
  manifest: readonly AreaBoardManifestRow[],
  ability: AreaAbility,
  group: string | null,
): readonly AreaBoardManifestRow[] {
  const groupKnown = group !== null && manifest.some((row) => row.group === group);
  return manifest.filter((row) => matchesAbility(row, ability) && (!groupKnown || row.group === group));
}

function cssString(value: string): string {
  return value.replace(/["\\]/gu, "");
}

/**
 * Pure: the style rule for one board: rows a filter drops are hidden, and unless the board is
 * expanded, kept rows past the cap (8 on phones, 10 from md) are hidden too. Server HTML holds
 * every row; the first render already carries the default cap, so nothing jumps on hydration.
 */
export function areaBoardCss(input: Readonly<{
  scope: string;
  manifest: readonly AreaBoardManifestRow[];
  ability: AreaAbility;
  group: string | null;
  expanded: boolean;
}>): string {
  const visible = boardVisibleRows(input.manifest, input.ability, input.group);
  const kept = new Set(visible.map((row) => row.slug));
  const at = `[data-area-board="${cssString(input.scope)}"]`;
  const select = (rows: readonly AreaBoardManifestRow[]) => rows.map((row) => `${at} [data-area-row="${cssString(row.slug)}"]`).join(",");
  const parts: string[] = [];
  const dropped = input.manifest.filter((row) => !kept.has(row.slug));
  if (dropped.length > 0) parts.push(`${select(dropped)}{display:none}`);
  if (!input.expanded) {
    const phone = visible.slice(AREA_BOARD_CAP_PHONE);
    const desktop = visible.slice(AREA_BOARD_CAP_DESKTOP);
    if (phone.length > 0) parts.push(`@media (max-width:767.98px){${select(phone)}{display:none}}`);
    if (desktop.length > 0) parts.push(`@media (min-width:768px){${select(desktop)}{display:none}}`);
  }
  return parts.join("");
}

/** The board's table columns on desktop, by stat family. */
export function boardColumns(kind: AreaRowStats["kind"]): readonly Readonly<{ key: string; label: string }>[] {
  switch (kind) {
    case "surf": return [{ key: "swell", label: "Swell" }, { key: "wind", label: "Wind" }, { key: "water", label: "Water" }];
    case "snow": return [{ key: "terrain", label: "Terrain" }, { key: "base", label: "Base" }, { key: "low", label: "Low" }];
    case "wind": return [{ key: "wind", label: "Wind" }, { key: "water", label: "Water" }];
    case "water": return [{ key: "water", label: "Water" }, { key: "swell", label: "Swell" }];
    case "trail": return [{ key: "low", label: "Low" }, { key: "wind", label: "Wind" }];
    case "sailing": return [{ key: "wind", label: "Wind" }, { key: "venue", label: "Venue" }];
  }
}

/** Rounded, with a true minus sign for negatives (area-format's formatNumber). */
const round = formatNumber;

/** One desktop cell for a row, or null when the reading has no value. */
export function boardCell(stats: AreaRowStats, key: string): string | null {
  const value = (n: number | null, unit: string, places = 0) => (n === null ? null : `${round(n, places)}${unit}`);
  switch (key) {
    case "swell":
      if (stats.kind === "surf") return stats.swellM === null ? null : `${round(stats.swellM, 1)} m${stats.periodS !== null ? ` at ${round(stats.periodS)} s` : ""}`;
      return stats.kind === "water" ? value(stats.swellM, " m", 1) : null;
    case "wind":
      if (stats.kind === "surf") return stats.windKmh === null ? null : `${round(stats.windKmh)} km/h${stats.windRelation ? ` ${stats.windRelation}` : ""}`;
      return "windKmh" in stats ? value(stats.windKmh, " km/h") : null;
    case "water":
      return "waterC" in stats ? value(stats.waterC, "°C") : null;
    case "terrain":
      return stats.kind === "snow" && stats.extent !== null ? `${round(stats.extent * 100)}%` : null;
    case "base":
      return stats.kind === "snow" ? value(stats.depthCm, " cm") : null;
    case "low":
      return stats.kind === "snow" || stats.kind === "trail" ? value(stats.tempMinC, "°C", 1) : null;
    case "venue":
      return stats.kind === "sailing" && stats.venueType ? stats.venueType.replace(/_/gu, " ") : null;
    default:
      return null;
  }
}

/** Phone rows have no column heads, so terrain, base and low readings carry their own words. */
const SHORT_LABEL: Readonly<Record<string, (value: string) => string>> = {
  terrain: (value) => `${value} open`,
  base: (value) => `base ${value}`,
  low: (value) => `low ${value}`,
};

/**
 * The phone row's short stats line: "1.5 m at 8 s · 13 km/h onshore · 16°C" for surf (the units
 * say what each is), "16% open · base 0 cm · low −5.5°C" for snow.
 */
export function boardStatsShort(stats: AreaRowStats): string {
  return boardColumns(stats.kind)
    .flatMap((column) => {
      const cell = boardCell(stats, column.key);
      if (cell === null) return [];
      const label = SHORT_LABEL[column.key];
      return [label ? label(cell) : cell];
    })
    .join(" · ");
}

export type AreaDifficultyChip = Readonly<{ label: string; tone: "beginner" | "intermediate" | "experienced" }>;

/** Surf and bodyboarding rows: "Beginner", "Intermediate" or "Experienced only". */
export function difficultyChip(row: Pick<AreaRow, "difficulty" | "experiencedOnly">): AreaDifficultyChip | null {
  if (row.experiencedOnly) return { label: "Experienced only", tone: "experienced" };
  if (row.difficulty === "beginner") return { label: "Beginner", tone: "beginner" };
  if (row.difficulty === "intermediate") return { label: "Intermediate", tone: "intermediate" };
  return null;
}

// ── Off-season countdown timeline ───────────────────────────────────────

export type CountdownMark = Readonly<{
  key: "first" | "most" | "snow";
  /** 0 to 100, left to right. */
  pct: number;
  label: string;
}>;

/**
 * Pure: the timeline from today to the median typical opening: the first resort's opening, the
 * median opening (the end of the line) and, when snow is forecast within the week, a snow mark.
 * Null when there is no median opening to count to.
 */
export function countdownTimeline(input: Readonly<{
  countdown: AreaViewCountdown;
  resorts: readonly AreaViewResort[];
  todayISO: string | null;
  snowPeak: Readonly<{ snowCm: number; dateISO: string }> | null;
}>): Readonly<{ totalDays: number; marks: readonly CountdownMark[] }> | null {
  const { countdown } = input;
  if (!countdown.mostOpensShort) return null;
  const most = input.resorts.find((resort) => resort.opensShort === countdown.mostOpensShort && resort.daysUntil !== null);
  const totalDays = most?.daysUntil ?? (countdown.weeksUntilMost !== null ? countdown.weeksUntilMost * 7 : null);
  if (totalDays === null || totalDays <= 0) return null;
  const marks: CountdownMark[] = [];
  const first = countdown.first;
  // A first opening right next to the median would collide with its label: the end mark says it.
  if (first && first.opensShort !== countdown.mostOpensShort && first.daysUntil > 0 && first.daysUntil / totalDays <= 0.82) {
    marks.push({ key: "first", pct: Math.round((first.daysUntil / totalDays) * 1000) / 10, label: first.opensShort });
  }
  marks.push({ key: "most", pct: 100, label: countdown.mostOpensShort });
  if (input.snowPeak && input.todayISO && input.snowPeak.snowCm >= 1) {
    const offset = daysBetween(input.todayISO, input.snowPeak.dateISO);
    if (offset >= 0 && offset < totalDays) {
      marks.push({ key: "snow", pct: Math.max(4, Math.round((offset / totalDays) * 1000) / 10), label: `Snow up high ${weekdayShortOf(input.snowPeak.dateISO)}` });
    }
  }
  return { totalDays, marks };
}

/** "Usually early December." from the season reader's "usually opens in early December". */
export function opensSentence(opensLabel: string | null): string | null {
  if (!opensLabel) return null;
  const trimmed = opensLabel.trim().replace(/\.$/u, "");
  return `${capitalise(trimmed.replace(/^usually opens in /iu, "usually "))}.`;
}

// ── Snow watch chart geometry ───────────────────────────────────────────

export type SnowChartBar = Readonly<{
  x: number;
  y: number;
  width: number;
  height: number;
  /** Centre of the column. */
  cx: number;
  snowLabel: string | null;
  /** The weekday ("Sat"): an ISR page can outlive local midnight, so no bar says "Today". */
  dayLabel: string;
  tempLabel: string | null;
}>;

export type SnowChart = Readonly<{
  width: number;
  height: number;
  plot: Readonly<{ left: number; right: number; top: number; bottom: number }>;
  snowTicks: readonly Readonly<{ y: number; label: string }>[];
  freezeTicks: readonly Readonly<{ y: number; label: string }>[];
  bars: readonly SnowChartBar[];
  /** Freezing level points, null where a day has none. */
  line: readonly Readonly<{ x: number; y: number }>[];
  /**
   * The forecast height's dashed line and its label, set at the right end of the plot above or
   * below the line, whichever keeps clear of the freezing level line there.
   */
  heightLine: Readonly<{ y: number; label: string; labelY: number }> | null;
}>;

function snowText(cm: number): string {
  if (cm < 0.5) return "";
  return cm < 10 ? round(cm, 1) : round(cm);
}

/**
 * Pure: the snow watch chart for one forecast point: snowfall bars (cm, left axis), the freezing
 * level line (right axis, whole thousands) and the forecast height. Sized for its viewBox.
 */
export function snowChart(input: Readonly<{
  days: readonly AreaViewDay[];
  weather: readonly AreaViewWeatherDay[];
  elevationM: number | null;
  width: number;
  height: number;
}>): SnowChart {
  const { width, height } = input;
  const plot = { left: 30, right: width - 40, top: 18, bottom: height - 46 };
  const byDate = new Map(input.weather.map((day) => [day.dateISO, day]));
  const rows = input.days.map((day) => ({ day, weather: byDate.get(day.dateISO) ?? null }));
  const maxSnow = Math.max(0, ...rows.map((row) => row.weather?.snowCm ?? 0));
  const snowTop = Math.max(8, Math.ceil(maxSnow / 4) * 4);
  const freezes = rows.flatMap((row) => (row.weather?.freezingM != null ? [row.weather.freezingM] : []));
  const levels = input.elevationM !== null ? [...freezes, input.elevationM] : freezes;
  const lo = levels.length > 0 ? Math.floor(Math.min(...levels) / 1000) * 1000 : 0;
  let hi = levels.length > 0 ? Math.ceil(Math.max(...levels) / 1000) * 1000 : 4000;
  if (hi - lo < 2000) {
    hi = lo + 2000;
  }
  if (hi === lo) hi = lo + 1000;
  const span = plot.bottom - plot.top;
  const ySnow = (cm: number) => plot.bottom - (Math.max(0, cm) / snowTop) * span;
  const yFreeze = (m: number) => plot.bottom - ((m - lo) / (hi - lo)) * span;
  const step = (plot.right - plot.left) / Math.max(1, rows.length);
  const barWidth = Math.min(40, step * 0.56);
  const bars = rows.map((row, index) => {
    const cx = plot.left + step * index + step / 2;
    const snow = row.weather?.snowCm ?? 0;
    const shown = snow >= 0.5;
    const top = shown ? ySnow(snow) : plot.bottom - 3;
    const w = row.weather;
    const temp = w && w.maxC !== null && w.minC !== null ? `${round(w.maxC)}/${round(w.minC)}°` : null;
    return {
      x: Math.round((cx - barWidth / 2) * 10) / 10,
      y: Math.round(top * 10) / 10,
      width: Math.round(barWidth * 10) / 10,
      height: Math.round((plot.bottom - top) * 10) / 10,
      cx: Math.round(cx * 10) / 10,
      snowLabel: shown ? snowText(snow) : null,
      dayLabel: row.day.weekday,
      tempLabel: temp,
    };
  });
  const line = rows.flatMap((row, index) => (row.weather?.freezingM != null
    ? [{ x: bars[index].cx, y: Math.round(yFreeze(row.weather.freezingM) * 10) / 10 }]
    : []));
  const freezeTicks = [lo, (lo + hi) / 2, hi].map((m) => ({ y: Math.round(yFreeze(m) * 10) / 10, label: `${round(m / 1000, 1)}k` }));
  const snowTicks = [0, snowTop / 2, snowTop].map((cm) => ({ y: Math.round(ySnow(cm) * 10) / 10, label: round(cm) }));
  const heightLine = (() => {
    if (input.elevationM === null || input.elevationM < lo || input.elevationM > hi) return null;
    const y = Math.round(yFreeze(input.elevationM) * 10) / 10;
    // The label sits over the right half of the plot: it goes above the dashed line unless the
    // freezing level line passes within 16 units above it there, then below.
    const half = (plot.left + plot.right) / 2;
    const near = line.filter((point) => point.x >= half);
    const crowdedAbove = near.some((point) => point.y <= y && y - point.y < 16);
    const crowdedBelow = near.some((point) => point.y >= y && point.y - y < 16);
    const labelY = crowdedAbove && !crowdedBelow ? Math.min(plot.bottom - 3, y + 14) : Math.max(plot.top + 9, y - 5);
    return { y, label: `Forecast height ${thousands(input.elevationM)} m`, labelY: Math.round(labelY * 10) / 10 };
  })();
  return { width, height, plot, snowTicks, freezeTicks, bars, line, heightLine };
}

/** Pure: an honest one or two sentence summary of a forecast point's week. */
export function snowSummary(input: Readonly<{ name: string; weather: readonly AreaViewWeatherDay[] }>): string {
  const days = input.weather;
  let peak: AreaViewWeatherDay | null = null;
  for (const day of days) if (day.snowCm !== null && day.snowCm >= 0.5 && (peak === null || day.snowCm > (peak.snowCm ?? 0))) peak = day;
  const freezes = days.filter((day) => day.freezingM !== null);
  const roundTo50 = (m: number) => thousands(Math.round(m / 50) * 50);
  const parts: string[] = [];
  if (peak && peak.snowCm !== null) {
    const rain = peak.rainMm !== null && peak.rainMm >= 5 ? `, with about ${Math.round(peak.rainMm)} mm of rain lower down` : "";
    parts.push(`${weekdayLong(peak.dateISO)} brings about ${snowText(peak.snowCm)} cm of snow at the ${input.name} forecast point${rain}.`);
  } else {
    parts.push(`No snow is forecast at the ${input.name} forecast point in the next seven days.`);
  }
  if (freezes.length >= 2) {
    const first = freezes[0].freezingM as number;
    const last = freezes[freezes.length - 1].freezingM as number;
    if (Math.abs(first - last) >= 300) {
      parts.push(`The freezing level ${last < first ? "falls" : "rises"} from about ${roundTo50(first)} m to ${roundTo50(last)} m by ${weekdayLong(freezes[freezes.length - 1].dateISO)}.`);
    }
  }
  if (peak) parts.push("Early season snow often melts again: a good sign, not the start of the season.");
  return parts.join(" ");
}

/** "up to 7.6 cm by Fri" over the week, or "no snow forecast". */
export function snowTotalLine(weather: readonly AreaViewWeatherDay[]): string {
  const total = weather.reduce((sum, day) => sum + (day.snowCm ?? 0), 0);
  if (total < 0.5) return "no snow forecast";
  const lastSnow = [...weather].reverse().find((day) => (day.snowCm ?? 0) >= 0.5);
  return `up to ${snowText(total)} cm${lastSnow ? ` by ${weekdayShortOf(lastSnow.dateISO)}` : ""}`;
}

/** The snow watch's heading: "Snow up high on Thursday", or "First snow watch". */
export function snowWatchHeading(watch: Pick<AreaViewSnowWatch, "peak">): string {
  return watch.peak && watch.peak.snowCm >= 1 ? `Snow up high on ${weekdayLong(watch.peak.dateISO)}` : "First snow watch";
}
